const XML_ATTRIBUTE_PATTERN = /([A-Za-z_:][\w:.-]*)="([^"]*)"/g;
const XML_PROPERTY_PATTERN = /<property\b([^>]*?)(?:\/>|>([\s\S]*?)<\/property>)/g;
const XML_LAYER_PATTERN = /<layer\b([^>]*)>([\s\S]*?)<\/layer>/g;
const XML_OBJECT_GROUP_PATTERN = /<objectgroup\b([^>]*)>([\s\S]*?)<\/objectgroup>/g;
const XML_OBJECT_PATTERN = /<object\b([^>]*?)(?:\/>|>([\s\S]*?)<\/object>)/g;
const XML_TILE_PATTERN = /<tile\b([^>]*?)>([\s\S]*?)<\/tile>/g;

export const CANONICAL_MAP_SCHEMA = 'mmo-map-v1';
export const DEFAULT_OBJECT_ASSET_MANIFEST_PATH = '/visual-assets/objects/object-assets.json';

export const TILE_LEGEND = Object.freeze({
  floor: 0,
  wall: 1,
  portal: 2,
  path: 3,
});

export const BIOME_LEGEND = Object.freeze({
  default: 0,
  grass: 1,
  sand: 2,
  water: 3,
  dirt: 4,
  grassy_dirt: 5,
  meadow_dirt: 15,
  soft_grass: 14,
  brick: 6,
  brick2: 7,
  cobblestone_path: 8,
  realistic_grass: 9,
  realistic_grass2: 10,
  realistic_floor: 11,
  realistic_water: 12,
  realistic_dirt: 13,
});

const RESERVED_OBJECT_LAYER_NAMES = new Set(['collision', 'biomes', 'spawns', 'portals']);

export function canonicalizeMapDocument(input) {
  if (!isCanonicalMapDocument(input)) {
    throw new Error(`Map JSON must declare schema '${CANONICAL_MAP_SCHEMA}'.`);
  }
  return normalizeCanonicalMapDocument(input);
}

export function isCanonicalMapDocument(input) {
  return Boolean(input && typeof input === 'object' && input.schema === CANONICAL_MAP_SCHEMA);
}

export function normalizeCanonicalMapDocument(input) {
  if (!input || typeof input !== 'object') {
    throw new Error('Canonical map document must be an object.');
  }

  const doc = /** @type {Record<string, unknown>} */ (input);
  const rules = doc.rules && typeof doc.rules === 'object'
    ? { .../** @type {Record<string, unknown>} */ (doc.rules) }
    : {};
  const portals = normalizePortals(doc.portals ?? []);
  const normalized = {
    schema: CANONICAL_MAP_SCHEMA,
    mapId: readString(doc.mapId, 'mapId'),
    name: readString(doc.name, 'name'),
    ...(readOptionalString(doc.notes) ? { notes: readOptionalString(doc.notes) } : {}),
    mapKind: readString(doc.mapKind, 'mapKind'),
    gridType: readString(doc.gridType, 'gridType'),
    width: readNumber(doc.width, 'width'),
    height: readNumber(doc.height, 'height'),
    tiles: normalizeLegendBlob(doc.tiles, 'tiles', TILE_LEGEND),
    spawnPoints: normalizeSpawnPoints(doc.spawnPoints ?? {}),
    rules,
    portals,
    objects: Array.isArray(doc.objects) ? doc.objects.map(normalizeCanonicalObjectPlacement) : [],
    render: normalizeRenderSection(doc.render ?? null),
    backend: normalizeBackendSection(doc.backend ?? null),
  };
  const biomeStorage = normalizeBiomeStorage(doc, normalized.width, normalized.height);
  normalized.defaultBiome = biomeStorage.defaultBiome;
  normalized.biomeRegions = biomeStorage.biomeRegions;
  normalized.biomeOverrides = biomeStorage.biomeOverrides;
  normalized.biomes = biomeStorage.biomes;
  for (const portal of portals) {
    const index = (portal.y * normalized.width) + portal.x;
    if (index >= 0 && index < normalized.tiles.data.length) {
      normalized.tiles.data[index] = TILE_LEGEND.portal;
    }
  }
  return normalized;
}

export function buildCanonicalMapDocumentFromAuthoritative(source, render = null) {
  if (!source || typeof source !== 'object') {
    throw new Error('Authoritative map definition is required.');
  }
  if (!source.payload || typeof source.payload !== 'object' || source.payload.type !== 'blob') {
    throw new Error(`Map '${source.mapId}' cannot be exported because it is not a blob-backed map.`);
  }

  const rawRules = source.rules && typeof source.rules === 'object'
    ? { ...source.rules }
    : {};

  return normalizeCanonicalMapDocument({
    schema: CANONICAL_MAP_SCHEMA,
    mapId: source.mapId,
    name: source.name,
    ...(readOptionalString(source.notes) ? { notes: readOptionalString(source.notes) } : {}),
    mapKind: source.mapKind,
    gridType: source.gridType,
    width: source.width,
    height: source.height,
    tiles: {
      legend: { ...TILE_LEGEND },
      data: Array.from(source.payload.tiles),
    },
    biomes: {
      legend: { ...BIOME_LEGEND },
      data: Array.from(source.payload.biomes),
    },
    spawnPoints: normalizeSpawnPoints(source.spawnPoints ?? {}),
    rules: rawRules,
    portals: normalizePortals(source.portals ?? []),
    objects: Array.isArray(source.objects) ? source.objects.map(normalizeCanonicalObjectPlacement) : [],
    render: normalizeRenderSection(render),
    backend: normalizeBackendSection(source.backend ?? null),
  });
}

export async function buildRenderSectionFromTmx(options) {
  const bundle = await parseTiledMapBundle(options);
  return {
    displayTileWidth: readOptionalNumber(bundle.mapProperties.displayTileWidth),
    displayTileHeight: readOptionalNumber(bundle.mapProperties.displayTileHeight),
    objectAssetManifest: options.objectAssetManifestPath ?? DEFAULT_OBJECT_ASSET_MANIFEST_PATH,
    tiled: {
      runtimeTmxPath: options.runtimeTmxPath ?? inferPublicPath(options.tmxPath),
      mapTileWidth: bundle.mapTileWidth,
      mapTileHeight: bundle.mapTileHeight,
      tilesets: bundle.tilesets.map((tileset) => ({
        name: tileset.name,
        source: inferPublicPath(tileset.resolvedSource) ?? tileset.resolvedSource,
        firstGid: tileset.firstGid,
        tileCount: tileset.tileCount,
      })),
      layers: bundle.visualLayers.map((layer) => (
        layer.kind === 'tile'
          ? {
              kind: 'tile',
              name: layer.name,
              visible: layer.visible,
              opacity: layer.opacity,
              width: layer.width,
              height: layer.height,
              data: layer.data,
            }
          : {
              kind: 'object',
              name: layer.name,
              visible: layer.visible,
              opacity: layer.opacity,
            }
      )),
    },
  };
}

export async function parseTiledMapBundle({ tmxText, tmxPath, loadText }) {
  const mapMatch = tmxText.match(/<map\b([^>]*)>([\s\S]*)<\/map>/);
  if (!mapMatch) {
    throw new Error(`TMX '${tmxPath}' does not contain a <map> root element.`);
  }

  const mapAttributes = parseXmlAttributes(mapMatch[1]);
  const mapBody = mapMatch[2];
  const orientation = mapAttributes.orientation ?? 'orthogonal';
  if (orientation !== 'orthogonal') {
    throw new Error(`TMX '${tmxPath}' uses unsupported orientation '${orientation}'.`);
  }

  const mapProperties = parsePropertiesBlock(extractFirstPropertiesBlock(mapBody));
  const width = parseInteger(mapAttributes.width, `map width in '${tmxPath}'`);
  const height = parseInteger(mapAttributes.height, `map height in '${tmxPath}'`);
  const mapTileWidth = parseInteger(mapAttributes.tilewidth, `map tilewidth in '${tmxPath}'`);
  const mapTileHeight = parseInteger(mapAttributes.tileheight, `map tileheight in '${tmxPath}'`);

  const tilesets = await parseExternalTilesets(tmxText, tmxPath, loadText);
  const tilesetByName = new Map(tilesets.map((tileset) => [tileset.name, tileset]));

  const tiles = new Array(width * height).fill(TILE_LEGEND.floor);
  const biomes = new Array(width * height).fill(BIOME_LEGEND.default);
  const spawnPoints = {};
  const portals = [];
  const objects = [];
  const visualLayers = [];

  for (const match of mapBody.matchAll(XML_LAYER_PATTERN)) {
    const attributes = parseXmlAttributes(match[1]);
    const body = match[2];
    const name = attributes.name ?? 'layer';
    const dataBlock = body.match(/<data\b([^>]*)>([\s\S]*?)<\/data>/);
    if (!dataBlock) {
      throw new Error(`Layer '${name}' in '${tmxPath}' is missing <data>.`);
    }
    const dataAttributes = parseXmlAttributes(dataBlock[1]);
    if (dataAttributes.encoding !== 'csv') {
      throw new Error(`Layer '${name}' in '${tmxPath}' must use CSV encoding.`);
    }
    const layerWidth = parseInteger(attributes.width, `layer '${name}' width`);
    const layerHeight = parseInteger(attributes.height, `layer '${name}' height`);
    const data = parseCsvNumbers(dataBlock[2]);
    if (data.length !== layerWidth * layerHeight) {
      throw new Error(`Layer '${name}' in '${tmxPath}' has ${data.length} cells, expected ${layerWidth * layerHeight}.`);
    }
    visualLayers.push({
      kind: 'tile',
      name,
      visible: parseVisibleFlag(attributes.visible),
      opacity: parseOpacity(attributes.opacity),
      width: layerWidth,
      height: layerHeight,
      data,
    });
  }

  for (const match of mapBody.matchAll(XML_OBJECT_GROUP_PATTERN)) {
    const attributes = parseXmlAttributes(match[1]);
    const body = match[2];
    const layerName = attributes.name ?? 'objects';
    const visible = parseVisibleFlag(attributes.visible);
    const opacity = parseOpacity(attributes.opacity);
    const layerObjects = Array.from(body.matchAll(XML_OBJECT_PATTERN)).map((objectMatch) =>
      parseTiledObject(objectMatch[1], objectMatch[2] ?? '', {
        layerName,
        mapTileWidth,
        mapTileHeight,
        tilesets,
      })
    );

    if (layerName === 'spawns') {
      for (const object of layerObjects) {
        if (!object.name) {
          continue;
        }
        spawnPoints[object.name] = {
          x: object.mapX,
          y: object.mapY,
        };
      }
      continue;
    }

    if (layerName === 'portals') {
      for (const object of layerObjects) {
        const destMapId = readOptionalString(object.properties.destMapId ?? object.properties.dest_map_id);
        const destX = readOptionalNumber(object.properties.destX ?? object.properties.dest_x);
        const destY = readOptionalNumber(object.properties.destY ?? object.properties.dest_y);
        if (!destMapId || destX === null || destY === null) {
          continue;
        }
        portals.push({
          x: object.mapX,
          y: object.mapY,
          destMapId,
          destX,
          destY,
        });
      }
      continue;
    }

    if (layerName === 'collision') {
      for (const object of layerObjects) {
        forEachTileInObjectBounds(object, width, height, (index) => {
          tiles[index] = TILE_LEGEND.wall;
        });
      }
      continue;
    }

    if (layerName === 'biomes') {
      for (const object of layerObjects) {
        const biomeName = readOptionalString(object.properties.biome);
        const biomeValue = biomeName && biomeName in BIOME_LEGEND ? BIOME_LEGEND[biomeName] : BIOME_LEGEND.default;
        forEachTileInObjectBounds(object, width, height, (index) => {
          biomes[index] = biomeValue;
        });
      }
      continue;
    }

    visualLayers.push({
      kind: 'object',
      name: layerName,
      visible,
      opacity,
    });

    for (const object of layerObjects) {
      if (!object.asset) {
        continue;
      }
      objects.push({
        id: object.id || `${layerName}_${objects.length + 1}`,
        asset: object.asset,
        x: object.mapX,
        y: object.mapY,
        offsetX: readOptionalFloat(object.properties.offsetX) ?? 0,
        offsetY: readOptionalFloat(object.properties.offsetY) ?? 0,
        sizeMultiplier: readOptionalFloat(object.properties.sizeMultiplier) ?? 1,
        layer: layerName,
      });
    }
  }

  for (const portal of portals) {
    const index = (portal.y * width) + portal.x;
    if (index >= 0 && index < tiles.length) {
      tiles[index] = TILE_LEGEND.portal;
    }
  }

  return {
    width,
    height,
    mapTileWidth,
    mapTileHeight,
    mapProperties,
    tilesets,
    tilesetByName,
    visualLayers,
    tiles,
    biomes,
    spawnPoints,
    portals,
    objects,
  };
}

export async function buildCanonicalMapDocumentFromTmx(options) {
  const bundle = await parseTiledMapBundle(options);
  const mapId = readOptionalString(bundle.mapProperties.mapId) ?? stemFromPath(options.tmxPath);
  const displayTileWidth = readOptionalNumber(bundle.mapProperties.displayTileWidth);
  const displayTileHeight = readOptionalNumber(bundle.mapProperties.displayTileHeight);
  const visibilityMode = readOptionalString(bundle.mapProperties.visibilityMode) ?? 'full_map';
  const mapKind = readOptionalString(bundle.mapProperties.mapKind) ?? 'town';
  const gridType = readOptionalString(bundle.mapProperties.gridType) ?? 'square';

  const rules = {
    tileSource: 'blob',
    visibilityMode,
  };

  const objectAssetManifest = readOptionalString(bundle.mapProperties.objectAssetManifest)
    ?? options.objectAssetManifestPath
    ?? DEFAULT_OBJECT_ASSET_MANIFEST_PATH;

  return normalizeCanonicalMapDocument({
    schema: CANONICAL_MAP_SCHEMA,
    mapId,
    name: readOptionalString(bundle.mapProperties.name) ?? humanizeId(mapId),
    mapKind,
    gridType,
    width: bundle.width,
    height: bundle.height,
    tiles: {
      legend: { ...TILE_LEGEND },
      data: bundle.tiles,
    },
    biomes: {
      legend: { ...BIOME_LEGEND },
      data: bundle.biomes,
    },
    spawnPoints: bundle.spawnPoints,
    rules,
    portals: bundle.portals,
    objects: bundle.objects,
    render: {
      ...(displayTileWidth !== null ? { displayTileWidth } : {}),
      ...(displayTileHeight !== null ? { displayTileHeight } : {}),
      objectAssetManifest,
      tiled: {
        runtimeTmxPath: options.runtimeTmxPath
          ?? readOptionalString(bundle.mapProperties.runtimeTmxPath)
          ?? inferPublicPath(options.tmxPath),
        mapTileWidth: bundle.mapTileWidth,
        mapTileHeight: bundle.mapTileHeight,
        tilesets: bundle.tilesets.map((tileset) => ({
          name: tileset.name,
          source: inferPublicPath(tileset.resolvedSource) ?? tileset.resolvedSource,
          firstGid: tileset.firstGid,
          tileCount: tileset.tileCount,
        })),
        layers: bundle.visualLayers.map((layer) => (
          layer.kind === 'tile'
            ? {
                kind: 'tile',
                name: layer.name,
                visible: layer.visible,
                opacity: layer.opacity,
                width: layer.width,
                height: layer.height,
                data: layer.data,
              }
            : {
                kind: 'object',
                name: layer.name,
                visible: layer.visible,
                opacity: layer.opacity,
              }
        )),
      },
    },
  });
}

export async function generateTiledFilesFromMapDocument(document, options) {
  const doc = canonicalizeMapDocument(document);
  if (!doc.render?.tiled) {
    throw new Error(`Map '${doc.mapId}' is missing render.tiled data and cannot be converted to TMX.`);
  }

  const tmxPath = options.outputTmxPath;
  const tmxDirectory = dirname(tmxPath);
  const tmxName = basename(tmxPath);
  const mapTileWidth = doc.render.tiled.mapTileWidth ?? 32;
  const mapTileHeight = doc.render.tiled.mapTileHeight ?? 32;

  const objectAssetManifestPath = doc.render.objectAssetManifest ?? DEFAULT_OBJECT_ASSET_MANIFEST_PATH;
  const manifest = await options.loadJson(resolveManifestFilesystemPath(objectAssetManifestPath, options.projectRoot));
  const objectTilesetPath = `${stemFromPath(tmxName)}.objects.tsx`;

  const maxTileGid = doc.render.tiled.tilesets.reduce(
    (highest, tileset) => Math.max(highest, Number(tileset.firstGid) + Number(tileset.tileCount) - 1),
    0
  );
  const objectTilesetFirstGid = maxTileGid + 1;
  const objectTileset = buildObjectTilesetDocument({
    manifest,
    outputDirectory: tmxDirectory,
    outputPath: joinPath(tmxDirectory, objectTilesetPath),
    projectRoot: options.projectRoot,
  });

  const objectAssetByName = new Map(objectTileset.assets.map((asset, index) => [asset.name, { ...asset, tileId: index }]));
  const layerEntries = [];

  for (const layer of doc.render.tiled.layers) {
    if (layer.kind === 'tile') {
      layerEntries.push(renderTileLayerXml(layer));
    } else if (layer.kind === 'object') {
      const objects = doc.objects.filter((entry) => (entry.layer ?? 'objects') === layer.name);
      layerEntries.push(renderObjectLayerXml(layer.name, objects, {
        objectAssetByName,
        mapTileWidth,
        mapTileHeight,
        firstGid: objectTilesetFirstGid,
        visible: layer.visible,
        opacity: layer.opacity,
      }));
    }
  }

  layerEntries.push(renderCollisionLayerXml(doc, mapTileWidth, mapTileHeight));
  layerEntries.push(renderBiomeLayerXml(doc, mapTileWidth, mapTileHeight));
  layerEntries.push(renderSpawnLayerXml(doc, mapTileWidth, mapTileHeight));
  layerEntries.push(renderPortalLayerXml(doc, mapTileWidth, mapTileHeight));

  const mapProperties = [
    ['schema', CANONICAL_MAP_SCHEMA],
    ['mapId', doc.mapId],
    ['name', doc.name],
    ['mapKind', doc.mapKind],
    ['gridType', doc.gridType],
    ['visibilityMode', readOptionalString(doc.rules.visibilityMode) ?? 'full_map'],
    ['displayTileWidth', readOptionalNumber(doc.render.displayTileWidth)],
    ['displayTileHeight', readOptionalNumber(doc.render.displayTileHeight)],
    ['runtimeTmxPath', readOptionalString(doc.render.tiled.runtimeTmxPath)],
    ['objectAssetManifest', objectAssetManifestPath],
  ].filter((entry) => entry[1] !== null && entry[1] !== undefined);

  const tilesetEntries = doc.render.tiled.tilesets.map((tileset) =>
    ` <tileset firstgid="${escapeXml(String(tileset.firstGid))}" source="${escapeXml(relativePath(tmxDirectory, resolveSourceFilesystemPath(resolveTilesetSource(doc, tileset.source), options.projectRoot, tmxDirectory)))}"/>`
  );
  if (objectTileset.assets.length > 0) {
    tilesetEntries.push(` <tileset firstgid="${objectTilesetFirstGid}" source="${escapeXml(objectTilesetPath)}"/>`);
  }

  const tmxText = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<map version="1.10" tiledversion="1.11.0" orientation="orthogonal" renderorder="right-down" width="${doc.width}" height="${doc.height}" tilewidth="${mapTileWidth}" tileheight="${mapTileHeight}" infinite="0">`,
    renderPropertiesXml(mapProperties),
    ...tilesetEntries,
    ...layerEntries,
    '</map>',
    '',
  ].join('\n');

  return {
    tmx: {
      path: tmxPath,
      contents: tmxText,
    },
    objectTileset: objectTileset.assets.length > 0
      ? {
          path: joinPath(tmxDirectory, objectTilesetPath),
          contents: objectTileset.xml,
        }
      : null,
  };
}

function normalizeLegendBlob(input, label, defaultLegend) {
  if (!input || typeof input !== 'object') {
    throw new Error(`Map ${label} must be an object with legend/data.`);
  }
  const blob = /** @type {Record<string, unknown>} */ (input);
  const legendInput = blob.legend && typeof blob.legend === 'object'
    ? /** @type {Record<string, unknown>} */ (blob.legend)
    : defaultLegend;
  const legend = {};
  for (const [key, value] of Object.entries(legendInput)) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      legend[key] = Math.floor(value);
    }
  }
  return {
    legend,
    data: readNumberArray(blob.data, `${label}.data`),
  };
}

function normalizeBiomeStorage(input, width, height) {
  const expected = width * height;
  const source = /** @type {Record<string, unknown>} */ (input);
  const defaultBiome = normalizeBiomeId(source.defaultBiome ?? 'default', 'defaultBiome');
  const biomeRegions = normalizeBiomeRegions(source.biomeRegions ?? [], width, height);
  const biomeOverrides = normalizeBiomeOverrides(source.biomeOverrides ?? {}, width, height);

  if (source.biomes !== undefined && source.biomes !== null) {
    const biomes = normalizeLegendBlob(source.biomes, 'biomes', BIOME_LEGEND);
    if (biomes.data.length !== expected) {
      throw new Error(`Map biomes.data must contain ${expected} entries.`);
    }
    return {
      defaultBiome,
      biomeRegions,
      biomeOverrides,
      biomes,
    };
  }

  const data = new Array(expected).fill(BIOME_LEGEND[defaultBiome] ?? BIOME_LEGEND.default);
  for (const region of biomeRegions) {
    const biomeValue = BIOME_LEGEND[region.biome] ?? BIOME_LEGEND.default;
    for (let y = region.y; y < region.y + region.height; y += 1) {
      for (let x = region.x; x < region.x + region.width; x += 1) {
        data[(y * width) + x] = biomeValue;
      }
    }
  }
  for (const [key, biome] of Object.entries(biomeOverrides)) {
    const [xRaw, yRaw] = key.split(',');
    const x = Number.parseInt(xRaw ?? '', 10);
    const y = Number.parseInt(yRaw ?? '', 10);
    if (!Number.isInteger(x) || !Number.isInteger(y) || x < 0 || y < 0 || x >= width || y >= height) {
      throw new Error(`Map biomeOverrides key '${key}' is out of bounds for ${width}x${height}.`);
    }
    data[(y * width) + x] = BIOME_LEGEND[biome] ?? BIOME_LEGEND.default;
  }

  return {
    defaultBiome,
    biomeRegions,
    biomeOverrides,
    biomes: {
      legend: { ...BIOME_LEGEND },
      data,
    },
  };
}

function normalizeBiomeRegions(input, width, height) {
  if (!Array.isArray(input)) {
    throw new Error('Map biomeRegions must be an array.');
  }
  return input.map((region, index) => {
    if (!region || typeof region !== 'object') {
      throw new Error(`Map biomeRegions[${index}] must be an object.`);
    }
    const x = readNumber(region.x, `biomeRegions[${index}].x`);
    const y = readNumber(region.y, `biomeRegions[${index}].y`);
    const regionWidth = readNumber(region.width, `biomeRegions[${index}].width`);
    const regionHeight = readNumber(region.height, `biomeRegions[${index}].height`);
    const biome = normalizeBiomeId(region.biome, `biomeRegions[${index}].biome`);
    if (x < 0 || y < 0 || regionWidth <= 0 || regionHeight <= 0 || x + regionWidth > width || y + regionHeight > height) {
      throw new Error(`Map biomeRegions[${index}] is out of bounds for ${width}x${height}.`);
    }
    return { x, y, width: regionWidth, height: regionHeight, biome };
  });
}

function normalizeBiomeOverrides(input, width, height) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    throw new Error('Map biomeOverrides must be an object keyed by "x,y".');
  }
  const overrides = {};
  for (const [key, value] of Object.entries(input)) {
    const [xRaw, yRaw] = key.split(',');
    const x = Number.parseInt(xRaw ?? '', 10);
    const y = Number.parseInt(yRaw ?? '', 10);
    if (!Number.isInteger(x) || !Number.isInteger(y) || x < 0 || y < 0 || x >= width || y >= height) {
      throw new Error(`Map biomeOverrides key '${key}' is out of bounds for ${width}x${height}.`);
    }
    overrides[`${x},${y}`] = normalizeBiomeId(value, `biomeOverrides['${key}']`);
  }
  return overrides;
}

function normalizeBiomeId(value, label) {
  if (typeof value !== 'string') {
    throw new Error(`Map ${label} must be a biome id string.`);
  }
  const biome = value.trim().toLowerCase();
  if (!biome) {
    throw new Error(`Map ${label} must not be empty.`);
  }
  return biome;
}

function normalizeRenderSection(input) {
  if (!input || typeof input !== 'object') {
    return null;
  }
  const render = /** @type {Record<string, unknown>} */ (input);
  const normalized = {
    ...(typeof render.displayTileWidth === 'number' ? { displayTileWidth: readNumber(render.displayTileWidth, 'render.displayTileWidth') } : {}),
    ...(typeof render.displayTileHeight === 'number' ? { displayTileHeight: readNumber(render.displayTileHeight, 'render.displayTileHeight') } : {}),
    ...(typeof render.objectAssetManifest === 'string' ? { objectAssetManifest: render.objectAssetManifest } : {}),
    ...(Array.isArray(render.pathLayers) ? {
      pathLayers: render.pathLayers.map((layer) => ({
        layerId: readString(layer.layerId, 'render.pathLayers[].layerId'),
        atlasId: readString(layer.atlasId, 'render.pathLayers[].atlasId'),
        width: readNumber(layer.width, 'render.pathLayers[].width'),
        height: readNumber(layer.height, 'render.pathLayers[].height'),
        data: Array.isArray(layer.data)
          ? layer.data.map((cell) => {
            if (!cell || typeof cell !== 'object') {
              return null;
            }
            const mode = readString(cell.mode, 'render.pathLayers[].data[].mode');
            if (mode === 'autotile') {
              return {
                mode,
                ...(cell.surfaceTerrain === 'wall'
                  || cell.surfaceTerrain === 'portal'
                  || cell.surfaceTerrain === 'path'
                  || cell.surfaceTerrain === 'floor'
                  ? { surfaceTerrain: cell.surfaceTerrain }
                  : {}),
              };
            }
            if (mode !== 'fixed') {
              throw new Error(`Map render.pathLayers[].data[].mode must be 'autotile' or 'fixed'.`);
            }
            return {
              mode,
              variantId: readString(cell.variantId, 'render.pathLayers[].data[].variantId'),
              ...(cell.surfaceTerrain === 'wall'
                || cell.surfaceTerrain === 'portal'
                || cell.surfaceTerrain === 'path'
                || cell.surfaceTerrain === 'floor'
                ? { surfaceTerrain: cell.surfaceTerrain }
                : {}),
              flipHorizontal: cell.flipHorizontal === true,
              flipVertical: cell.flipVertical === true,
              rotationSteps: typeof cell.rotationSteps === 'number'
                ? Math.floor(readNumber(cell.rotationSteps, 'render.pathLayers[].data[].rotationSteps'))
                : 0,
            };
          })
          : [],
      }))
    } : {}),
    ...(Array.isArray(render.pathBorderContinuations) ? {
      pathBorderContinuations: render.pathBorderContinuations
        .filter((entry) => entry && typeof entry === 'object')
        .map((entry) => ({
          atlasId: readString(entry.atlasId, 'render.pathBorderContinuations[].atlasId'),
          x: readNumber(entry.x, 'render.pathBorderContinuations[].x'),
          y: readNumber(entry.y, 'render.pathBorderContinuations[].y'),
        }))
    } : {}),
    tileWidth: readNumber(render.tileWidth ?? 32, 'render.tileWidth'),
    tileHeight: readNumber(render.tileHeight ?? 32, 'render.tileHeight'),
    layers: Array.isArray(render.layers)
      ? render.layers.map((layer) => (
          layer.kind === 'object'
            ? {
                kind: 'object',
                name: readString(layer.name, 'render.layers[].name'),
                visible: layer.visible !== false,
                opacity: typeof layer.opacity === 'number' ? layer.opacity : 1,
              }
            : {
                kind: 'tile',
                name: readString(layer.name, 'render.layers[].name'),
                visible: layer.visible !== false,
                opacity: typeof layer.opacity === 'number' ? layer.opacity : 1,
                width: readNumber(layer.width, 'render.layers[].width'),
                height: readNumber(layer.height, 'render.layers[].height'),
                data: readNumberArray(layer.data, 'render.layers[].data'),
              }
        ))
      : [],
  };

  if (typeof render.imagePath === 'string') {
    return {
      ...normalized,
      columns: readNumber(render.columns ?? 1, 'render.columns'),
      imageWidth: readNumber(render.imageWidth ?? 0, 'render.imageWidth'),
      imageHeight: readNumber(render.imageHeight ?? 0, 'render.imageHeight'),
      imagePath: readString(render.imagePath, 'render.imagePath'),
    };
  }

  if (render.directTileImages && typeof render.directTileImages === 'object') {
    return {
      ...normalized,
      directTileImages: render.directTileImages,
    };
  }

  if (render.tiled && typeof render.tiled === 'object') {
    return {
      ...normalized,
      tiled: render.tiled,
    };
  }

  return {
    ...normalized,
  };
}

function normalizeBackendSection(input) {
  if (!input || typeof input !== 'object') {
    return {
      monsterSpawns: [],
      lootSpawns: [],
      regions: [],
      metadata: {},
    };
  }
  const backend = /** @type {Record<string, unknown>} */ (input);
  return {
    monsterSpawns: Array.isArray(backend.monsterSpawns) ? backend.monsterSpawns : [],
    lootSpawns: Array.isArray(backend.lootSpawns) ? backend.lootSpawns : [],
    regions: Array.isArray(backend.regions) ? backend.regions : [],
    metadata: backend.metadata && typeof backend.metadata === 'object' ? backend.metadata : {},
  };
}

function normalizeSpawnPoints(input) {
  const output = {};
  if (!input || typeof input !== 'object') {
    return output;
  }
  for (const [key, value] of Object.entries(input)) {
    if (!value || typeof value !== 'object') {
      continue;
    }
    output[key] = {
      x: readNumber(value.x, `spawnPoints.${key}.x`),
      y: readNumber(value.y, `spawnPoints.${key}.y`),
    };
  }
  return output;
}

function normalizePortals(input) {
  if (!Array.isArray(input)) {
    return [];
  }
  return input
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry) => ({
      x: readNumber(entry.x, 'portals[].x'),
      y: readNumber(entry.y, 'portals[].y'),
      destMapId: readString(entry.destMapId ?? entry.dest_map_id, 'portals[].destMapId'),
      destX: readNumber(entry.destX ?? entry.dest_x, 'portals[].destX'),
      destY: readNumber(entry.destY ?? entry.dest_y, 'portals[].destY'),
    }));
}

function normalizeCanonicalObjectPlacement(entry) {
  const asset = readString(entry.asset, 'objects[].asset');
  const x = readNumber(entry.x, 'objects[].x');
  const y = readNumber(entry.y, 'objects[].y');
  const layer = readString(entry.layer ?? 'objects', 'objects[].layer');
  return {
    id: typeof entry.id === 'string' && entry.id.trim()
      ? readString(entry.id, 'objects[].id')
      : `${asset}_${x}_${y}_${layer}`.replace(/[^a-zA-Z0-9_-]+/g, '_'),
    asset,
    x,
    y,
    offsetX: typeof entry.offsetX === 'number' ? readFloat(entry.offsetX, 'objects[].offsetX') : 0,
    offsetY: typeof entry.offsetY === 'number' ? readFloat(entry.offsetY, 'objects[].offsetY') : 0,
    sizeMultiplier: typeof entry.sizeMultiplier === 'number' && readFloat(entry.sizeMultiplier, 'objects[].sizeMultiplier') > 0
      ? readFloat(entry.sizeMultiplier, 'objects[].sizeMultiplier')
      : 1,
    flipHorizontal: entry.flipHorizontal === true,
    flipVertical: entry.flipVertical === true,
    rotationSteps: typeof entry.rotationSteps === 'number' ? Math.floor(readNumber(entry.rotationSteps, 'objects[].rotationSteps')) : 0,
    layer,
  };
}

async function parseExternalTilesets(tmxText, tmxPath, loadText) {
  const tilesetMatches = Array.from(tmxText.matchAll(/<tileset\b([^>]*?)\/>/g));
  const tilesets = [];
  for (const match of tilesetMatches) {
    const attributes = parseXmlAttributes(match[1]);
    const firstGid = parseInteger(attributes.firstgid, `tileset firstgid in '${tmxPath}'`);
    const source = attributes.source;
    if (!source) {
      continue;
    }
    const resolvedSource = resolveRelativePath(tmxPath, source);
    const tsxText = await loadText(resolvedSource);
    const tilesetMatch = tsxText.match(/<tileset\b([^>]*)>([\s\S]*)<\/tileset>/);
    if (!tilesetMatch) {
      throw new Error(`TSX '${resolvedSource}' does not contain a <tileset> root element.`);
    }
    const tilesetAttributes = parseXmlAttributes(tilesetMatch[1]);
    const tilesetBody = tilesetMatch[2];
    const name = tilesetAttributes.name ?? stemFromPath(source);
    const tileCount = parseTilesetTileCount(tilesetAttributes, tilesetBody, resolvedSource);
    const tileMetadataById = parseTilesetTileMetadata(tilesetBody);
    tilesets.push({
      name,
      source,
      resolvedSource,
      firstGid,
      tileCount,
      tileMetadataById,
    });
  }
  return tilesets;
}

function parseTilesetTileCount(attributes, body, sourcePath) {
  if (attributes.tilecount) {
    return parseInteger(attributes.tilecount, `tilecount in '${sourcePath}'`);
  }
  const tileMatches = Array.from(body.matchAll(XML_TILE_PATTERN));
  if (tileMatches.length === 0) {
    return 0;
  }
  const maxId = tileMatches.reduce((highest, match) => {
    const attrs = parseXmlAttributes(match[1]);
    return Math.max(highest, parseInteger(attrs.id, `tile id in '${sourcePath}'`));
  }, 0);
  return maxId + 1;
}

function parseTilesetTileMetadata(body) {
  const metadata = new Map();
  for (const match of body.matchAll(XML_TILE_PATTERN)) {
    const attrs = parseXmlAttributes(match[1]);
    const inner = match[2];
    const id = parseInteger(attrs.id, 'tile id');
    const imageMatch = inner.match(/<image\b([^>]*)\/>/);
    const properties = parsePropertiesBlock(extractFirstPropertiesBlock(inner));
    metadata.set(id, {
      type: attrs.type ?? null,
      properties,
      image: imageMatch ? parseXmlAttributes(imageMatch[1]) : null,
    });
  }
  return metadata;
}

function parseTiledObject(attributeSource, innerSource, context) {
  const attributes = parseXmlAttributes(attributeSource);
  const properties = parsePropertiesBlock(extractFirstPropertiesBlock(innerSource));
  const width = readOptionalNumber(attributes.width) ?? context.mapTileWidth;
  const height = readOptionalNumber(attributes.height) ?? context.mapTileHeight;
  const x = readOptionalNumber(attributes.x) ?? 0;
  const y = readOptionalNumber(attributes.y) ?? 0;
  const gid = readOptionalNumber(attributes.gid);

  let asset = readOptionalString(properties.assetName) ?? null;
  if (gid !== null) {
    const tileset = context.tilesets.find((candidate) => gid >= candidate.firstGid && gid < candidate.firstGid + candidate.tileCount);
    if (tileset) {
      const tileId = gid - tileset.firstGid;
      const tileMetadata = tileset.tileMetadataById.get(tileId);
      asset = readOptionalString(tileMetadata?.properties?.assetName)
        ?? readOptionalString(tileMetadata?.type)
        ?? asset;
    }
  }

  const propertyMapX = readOptionalNumber(properties.mapX);
  const propertyMapY = readOptionalNumber(properties.mapY);
  const mapX = propertyMapX ?? Math.max(0, Math.floor(x / context.mapTileWidth));
  const mapY = propertyMapY ?? Math.max(0, Math.floor((y - height) / context.mapTileHeight));

  return {
    id: readOptionalString(properties.id) ?? readOptionalString(attributes.name) ?? null,
    name: readOptionalString(attributes.name) ?? null,
    asset,
    cellWidth: context.mapTileWidth,
    cellHeight: context.mapTileHeight,
    width,
    height,
    x,
    y,
    mapX,
    mapY,
    properties,
  };
}

function forEachTileInObjectBounds(object, width, height, visit) {
  const startX = Math.max(0, object.mapX);
  const startY = Math.max(0, object.mapY);
  const spanX = Math.max(1, Math.round(object.width / Math.max(1, object.cellWidth)));
  const spanY = Math.max(1, Math.round(object.height / Math.max(1, object.cellHeight)));
  for (let y = startY; y < Math.min(height, startY + spanY); y += 1) {
    for (let x = startX; x < Math.min(width, startX + spanX); x += 1) {
      visit((y * width) + x);
    }
  }
}

function buildObjectTilesetDocument({ manifest, outputDirectory, outputPath, projectRoot }) {
  const assets = Array.isArray(manifest.assets) ? manifest.assets : [];
  const tileEntries = [];
  let maxWidth = 0;
  let maxHeight = 0;

  for (const [index, asset] of assets.entries()) {
    if (!asset || typeof asset !== 'object' || typeof asset.name !== 'string' || typeof asset.publicPath !== 'string') {
      continue;
    }
    const sourcePath = resolveSourceFilesystemPath(asset.publicPath, projectRoot, outputDirectory);
    const relativeImagePath = relativePath(dirname(outputPath), sourcePath);
    const tileWidth = typeof asset.width === 'number' ? asset.width : 64;
    const tileHeight = typeof asset.height === 'number' ? asset.height : 64;
    maxWidth = Math.max(maxWidth, tileWidth);
    maxHeight = Math.max(maxHeight, tileHeight);
    tileEntries.push({
      name: asset.name,
      relativeImagePath,
      width: tileWidth,
      height: tileHeight,
      xml: [
        ` <tile id="${index}" type="${escapeXml(asset.name)}">`,
        '  <properties>',
        `   <property name="assetName" value="${escapeXml(asset.name)}"/>`,
        '  </properties>',
        `  <image source="${escapeXml(relativeImagePath)}" width="${tileWidth}" height="${tileHeight}"/>`,
        ' </tile>',
      ].join('\n'),
    });
  }

  const xml = assets.length === 0
    ? ''
    : [
        '<?xml version="1.0" encoding="UTF-8"?>',
        `<tileset version="1.10" tiledversion="1.11.0" name="${escapeXml(stemFromPath(outputPath))}" tilewidth="${maxWidth || 64}" tileheight="${maxHeight || 64}" tilecount="${tileEntries.length}" columns="0">`,
        ...tileEntries.map((entry) => entry.xml),
        '</tileset>',
        '',
      ].join('\n');

  return {
    xml,
    assets: tileEntries,
  };
}

function renderTileLayerXml(layer) {
  return [
    ` <layer name="${escapeXml(layer.name)}" width="${layer.width}" height="${layer.height}"${renderLayerVisibilityAttributes(layer.visible, layer.opacity)}>`,
    '  <data encoding="csv">',
    `   ${layer.data.join(',')}`,
    '  </data>',
    ' </layer>',
  ].join('\n');
}

function renderObjectLayerXml(layerName, objects, context) {
  const objectEntries = objects
    .map((object, index) => {
      const asset = context.objectAssetByName.get(object.asset);
      if (!asset) {
        return null;
      }
      const gid = context.firstGid + asset.tileId;
      const offsetX = typeof object.offsetX === 'number' ? object.offsetX : 0;
      const offsetY = typeof object.offsetY === 'number' ? object.offsetY : 0;
      const sizeMultiplier = typeof object.sizeMultiplier === 'number' ? object.sizeMultiplier : 1;
      const x = (object.x + offsetX) * context.mapTileWidth;
      const y = (object.y + 1 + offsetY) * context.mapTileHeight;
      return [
        `  <object id="${index + 1}" name="${escapeXml(object.id)}" gid="${gid}" x="${x}" y="${y}" width="${asset.width}" height="${asset.height}">`,
        '   <properties>',
        `    <property name="id" value="${escapeXml(object.id)}"/>`,
        `    <property name="assetName" value="${escapeXml(object.asset)}"/>`,
        `    <property name="mapX" type="int" value="${object.x}"/>`,
        `    <property name="mapY" type="int" value="${object.y}"/>`,
        `    <property name="offsetX" type="float" value="${offsetX}"/>`,
        `    <property name="offsetY" type="float" value="${offsetY}"/>`,
        `    <property name="sizeMultiplier" type="float" value="${sizeMultiplier}"/>`,
        '   </properties>',
        '  </object>',
      ].join('\n');
    })
    .filter(Boolean);

  return [
    ` <objectgroup name="${escapeXml(layerName)}"${renderLayerVisibilityAttributes(context.visible, context.opacity)}>`,
    ...objectEntries,
    ' </objectgroup>',
  ].join('\n');
}

function renderCollisionLayerXml(document, tileWidth, tileHeight) {
  const objects = [];
  let objectId = 1;
  for (let y = 0; y < document.height; y += 1) {
    for (let x = 0; x < document.width; x += 1) {
      const index = (y * document.width) + x;
      if (document.tiles.data[index] !== TILE_LEGEND.wall) {
        continue;
      }
      objects.push([
        `  <object id="${objectId}" x="${x * tileWidth}" y="${(y + 1) * tileHeight}" width="${tileWidth}" height="${tileHeight}">`,
        '   <properties>',
        `    <property name="mapX" type="int" value="${x}"/>`,
        `    <property name="mapY" type="int" value="${y}"/>`,
        '   </properties>',
        '  </object>',
      ].join('\n'));
      objectId += 1;
    }
  }
  return [' <objectgroup name="collision" visible="0">', ...objects, ' </objectgroup>'].join('\n');
}

function renderBiomeLayerXml(document, tileWidth, tileHeight) {
  const objects = [];
  let objectId = 1;
  const inverseBiomeLegend = invertLegend(document.biomes.legend);
  for (let y = 0; y < document.height; y += 1) {
    for (let x = 0; x < document.width; x += 1) {
      const index = (y * document.width) + x;
      const biomeValue = document.biomes.data[index];
      if (biomeValue === BIOME_LEGEND.default) {
        continue;
      }
      const biomeName = inverseBiomeLegend.get(biomeValue) ?? 'default';
      objects.push([
        `  <object id="${objectId}" x="${x * tileWidth}" y="${(y + 1) * tileHeight}" width="${tileWidth}" height="${tileHeight}">`,
        '   <properties>',
        `    <property name="biome" value="${escapeXml(biomeName)}"/>`,
        `    <property name="mapX" type="int" value="${x}"/>`,
        `    <property name="mapY" type="int" value="${y}"/>`,
        '   </properties>',
        '  </object>',
      ].join('\n'));
      objectId += 1;
    }
  }
  return [' <objectgroup name="biomes" visible="0">', ...objects, ' </objectgroup>'].join('\n');
}

function renderSpawnLayerXml(document, tileWidth, tileHeight) {
  const objects = Object.entries(document.spawnPoints).map(([name, spawn], index) => [
    `  <object id="${index + 1}" name="${escapeXml(name)}" x="${spawn.x * tileWidth}" y="${(spawn.y + 1) * tileHeight}">`,
    '   <properties>',
    `    <property name="mapX" type="int" value="${spawn.x}"/>`,
    `    <property name="mapY" type="int" value="${spawn.y}"/>`,
    '   </properties>',
    '   <point/>',
    '  </object>',
  ].join('\n'));
  return [' <objectgroup name="spawns" visible="0">', ...objects, ' </objectgroup>'].join('\n');
}

function renderPortalLayerXml(document, tileWidth, tileHeight) {
  const objects = document.portals.map((portal, index) => [
    `  <object id="${index + 1}" x="${portal.x * tileWidth}" y="${(portal.y + 1) * tileHeight}">`,
    '   <properties>',
    `    <property name="destMapId" value="${escapeXml(portal.destMapId)}"/>`,
    `    <property name="destX" type="int" value="${portal.destX}"/>`,
    `    <property name="destY" type="int" value="${portal.destY}"/>`,
    `    <property name="mapX" type="int" value="${portal.x}"/>`,
    `    <property name="mapY" type="int" value="${portal.y}"/>`,
    '   </properties>',
    '   <point/>',
    '  </object>',
  ].join('\n'));
  return [' <objectgroup name="portals" visible="0">', ...objects, ' </objectgroup>'].join('\n');
}

function renderPropertiesXml(entries) {
  return [
    ' <properties>',
    ...entries.map(([name, value]) => {
      if (typeof value === 'number') {
        const type = Number.isInteger(value) ? 'int' : 'float';
        return `  <property name="${escapeXml(name)}" type="${type}" value="${value}"/>`;
      }
      return `  <property name="${escapeXml(name)}" value="${escapeXml(String(value))}"/>`;
    }),
    ' </properties>',
  ].join('\n');
}

function renderLayerVisibilityAttributes(visible, opacity) {
  const attributes = [];
  if (visible === false) {
    attributes.push(' visible="0"');
  }
  if (typeof opacity === 'number' && opacity >= 0 && opacity < 1) {
    attributes.push(` opacity="${opacity}"`);
  }
  return attributes.join('');
}

function parseXmlAttributes(source) {
  const attributes = {};
  for (const match of source.matchAll(XML_ATTRIBUTE_PATTERN)) {
    attributes[match[1]] = decodeXml(match[2]);
  }
  return attributes;
}

function extractFirstPropertiesBlock(source) {
  if (!source) {
    return null;
  }
  const match = source.match(/<properties>([\s\S]*?)<\/properties>/);
  return match ? match[1] : null;
}

function parsePropertiesBlock(source) {
  const properties = {};
  if (!source) {
    return properties;
  }
  for (const match of source.matchAll(XML_PROPERTY_PATTERN)) {
    const attributes = parseXmlAttributes(match[1]);
    const name = attributes.name;
    if (!name) {
      continue;
    }
    const rawValue = attributes.value ?? decodeXml((match[2] ?? '').trim());
    properties[name] = coercePropertyValue(rawValue, attributes.type ?? 'string');
  }
  return properties;
}

function coercePropertyValue(value, type) {
  if (type === 'int') {
    return Number.parseInt(String(value), 10);
  }
  if (type === 'float') {
    return Number.parseFloat(String(value));
  }
  if (type === 'bool') {
    return String(value) === 'true';
  }
  return value;
}

function parseCsvNumbers(source) {
  return source
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value.length > 0)
    .map((value) => {
      const parsed = Number.parseInt(value, 10);
      if (!Number.isFinite(parsed)) {
        throw new Error(`Invalid CSV integer '${value}'.`);
      }
      return parsed;
    });
}

function invertLegend(legend) {
  const inverse = new Map();
  for (const [key, value] of Object.entries(legend)) {
    inverse.set(value, key);
  }
  return inverse;
}

function parseVisibleFlag(value) {
  return value !== '0';
}

function parseOpacity(value) {
  if (value === undefined || value === null) {
    return 1;
  }
  const parsed = Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : 1;
}

function parseInteger(value, label) {
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed)) {
    throw new Error(`Invalid ${label}.`);
  }
  return parsed;
}

function readString(value, label) {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`Missing ${label}.`);
  }
  return value;
}

function readOptionalString(value) {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readNumber(value, label) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`Missing ${label}.`);
  }
  return Math.floor(value);
}

function readOptionalNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? Math.floor(value) : null;
}

function readFloat(value, label) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`Missing ${label}.`);
  }
  return value;
}

function readOptionalFloat(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readNumberArray(value, label) {
  if (!Array.isArray(value)) {
    throw new Error(`Missing ${label}.`);
  }
  return value.map((entry, index) => {
    if (typeof entry !== 'number' || !Number.isFinite(entry)) {
      throw new Error(`Invalid ${label}[${index}].`);
    }
    return Math.floor(entry);
  });
}

function escapeXml(value) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function decodeXml(value) {
  return value
    .replaceAll('&quot;', '"')
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&amp;', '&');
}

function dirname(path) {
  const normalized = path.replace(/\/+/g, '/');
  const index = normalized.lastIndexOf('/');
  if (index <= 0) {
    return '/';
  }
  return normalized.slice(0, index);
}

function basename(path) {
  const normalized = path.replace(/\/+/g, '/');
  const index = normalized.lastIndexOf('/');
  return index >= 0 ? normalized.slice(index + 1) : normalized;
}

function stemFromPath(path) {
  const file = basename(path);
  const dot = file.lastIndexOf('.');
  return dot >= 0 ? file.slice(0, dot) : file;
}

function humanizeId(value) {
  return value
    .split(/[_-]+/)
    .filter((part) => part.length > 0)
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(' ');
}

function normalizeSegments(path) {
  const leadingSlash = path.startsWith('/');
  const segments = path.split('/').filter((segment) => segment.length > 0 && segment !== '.');
  const normalized = [];
  for (const segment of segments) {
    if (segment === '..') {
      normalized.pop();
    } else {
      normalized.push(segment);
    }
  }
  return `${leadingSlash ? '/' : ''}${normalized.join('/')}`;
}

function resolveRelativePath(basePath, relativePath) {
  if (relativePath.startsWith('/')) {
    return normalizeSegments(relativePath);
  }
  return normalizeSegments(`${dirname(basePath)}/${relativePath}`);
}

function joinPath(left, right) {
  return normalizeSegments(`${left.replace(/\/$/, '')}/${right.replace(/^\//, '')}`);
}

function relativePath(fromDirectory, toPath) {
  const fromSegments = normalizeSegments(fromDirectory).split('/').filter(Boolean);
  const toSegments = normalizeSegments(toPath).split('/').filter(Boolean);
  let shared = 0;
  while (shared < fromSegments.length && shared < toSegments.length && fromSegments[shared] === toSegments[shared]) {
    shared += 1;
  }
  const up = new Array(fromSegments.length - shared).fill('..');
  const down = toSegments.slice(shared);
  const relative = [...up, ...down].join('/');
  return relative.length > 0 ? relative : '.';
}

function inferPublicPath(filesystemPath) {
  const marker = '/frontend/public/';
  const index = filesystemPath.indexOf(marker);
  if (index < 0) {
    return null;
  }
  return `/${filesystemPath.slice(index + marker.length)}`;
}

function resolveSourceFilesystemPath(sourcePath, projectRoot, fallbackDirectory) {
  if (sourcePath.startsWith('/')) {
    if (sourcePath.startsWith('/home/')) {
      return normalizeSegments(sourcePath);
    }
    return normalizeSegments(`${projectRoot}/frontend/public${sourcePath}`);
  }
  return normalizeSegments(`${fallbackDirectory}/${sourcePath}`);
}

function resolveManifestFilesystemPath(publicPath, projectRoot) {
  if (publicPath.startsWith('/')) {
    return normalizeSegments(`${projectRoot}/frontend/public${publicPath}`);
  }
  return normalizeSegments(`${projectRoot}/${publicPath}`);
}

function resolveTilesetSource(document, source) {
  if (source.startsWith('/')) {
    return source;
  }
  const runtimeTmxPath = readOptionalString(document.render?.tiled?.runtimeTmxPath);
  if (!runtimeTmxPath) {
    return source;
  }
  return resolveRelativePath(runtimeTmxPath, source);
}
